import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';

interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export type LoginResult = 'success' | 'failed' | 'new_password_required';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokensSubject = new BehaviorSubject<AuthTokens | null>(null);
  private cognitoUrl: string;
  private challengeSession: string | null = null;
  private challengeUsername: string | null = null;

  constructor(private http: HttpClient) {
    this.cognitoUrl = `https://cognito-idp.${environment.cognito.region}.amazonaws.com`;
  }

  get isAuthenticated(): boolean {
    return this.tokensSubject.value !== null;
  }

  get idToken(): string | null {
    return this.tokensSubject.value?.idToken ?? null;
  }

  get needsPasswordChange(): boolean {
    return this.challengeSession !== null;
  }

  async login(username: string, password: string): Promise<LoginResult> {
    try {
      const response = await fetch(this.cognitoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        },
        body: JSON.stringify({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: environment.cognito.clientId,
          AuthParameters: {
            USERNAME: username,
            PASSWORD: password
          }
        })
      });

      if (!response.ok) {
        return 'failed';
      }

      const data = await response.json();

      if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        this.challengeSession = data.Session;
        this.challengeUsername = username;
        return 'new_password_required';
      }

      const result = data.AuthenticationResult;
      this.tokensSubject.next({
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken
      });
      return 'success';
    } catch {
      return 'failed';
    }
  }

  async completeNewPassword(newPassword: string): Promise<boolean> {
    if (!this.challengeSession || !this.challengeUsername) return false;

    try {
      const response = await fetch(this.cognitoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
        },
        body: JSON.stringify({
          ChallengeName: 'NEW_PASSWORD_REQUIRED',
          ClientId: environment.cognito.clientId,
          ChallengeResponses: {
            USERNAME: this.challengeUsername,
            NEW_PASSWORD: newPassword
          },
          Session: this.challengeSession
        })
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const result = data.AuthenticationResult;
      this.tokensSubject.next({
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken: result.RefreshToken
      });
      this.challengeSession = null;
      this.challengeUsername = null;
      return true;
    } catch {
      return false;
    }
  }

  logout(): void {
    this.tokensSubject.next(null);
    this.challengeSession = null;
    this.challengeUsername = null;
  }

  async refreshSession(): Promise<boolean> {
    const tokens = this.tokensSubject.value;
    if (!tokens?.refreshToken) return false;

    try {
      const response = await fetch(this.cognitoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: environment.cognito.clientId,
          AuthParameters: {
            REFRESH_TOKEN: tokens.refreshToken
          }
        })
      });

      if (!response.ok) {
        this.logout();
        return false;
      }

      const data = await response.json();
      const result = data.AuthenticationResult;
      this.tokensSubject.next({
        idToken: result.IdToken,
        accessToken: result.AccessToken,
        refreshToken: tokens.refreshToken
      });
      return true;
    } catch {
      this.logout();
      return false;
    }
  }
}
